import { PERMISSION_STATUS } from '@bingads-webui-campaign-react/linkedin-utils';

export const COMPANY_PAGE_LINKING_ACTIONS = {
  START_AUTHENTICATION: 'START_AUTHENTICATION',
  COMPANY_PAGES_FETCHED: 'COMPANY_PAGES_FETCHED',
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  UPDATE_SELECTED_COMPANY_PAGE: 'UPDATE_SELECTED_COMPANY_PAGE',
  CONFIRM_LINK_COMPANY_PAGE: 'CONFIRM_LINK_COMPANY_PAGE',
  START_SAVING_COMPANY_PAGE: 'START_SAVING_COMPANY_PAGE',
  COMPANY_PAGE_SAVED: 'COMPANY_PAGE_SAVED',
  COMPANY_PAGE_SAVE_FAILED: 'COMPANY_PAGE_SAVE_FAILED',
  LOAD_PERMISSION_STATUS: 'LOAD_PERMISSION_STATUS',
  PERMISSION_STATUS_LOADED: 'PERMISSION_STATUS_LOADED',
  PERMISSION_STATUS_LOAD_FAILED: 'PERMISSION_STATUS_LOAD_FAILED',
  GO_BACK_AND_RESET_COMPANY_PAGE: 'GO_BACK_AND_RESET_COMPANY_PAGE',
  RESEND_PERMISSION_REQUEST: 'RESEND_PERMISSION_REQUEST',
  PERMISSION_REQUEST_RESENT: 'PERMISSION_REQUEST_RESENT',
  PERMISSION_REQUEST_RESEND_FAILED: 'PERMISSION_REQUEST_RESEND_FAILED',
  CLEAN_ERRORS: 'CLEAN_ERRORS',
  START_REFRESH_MEMBER_TOKEN: 'START_REFRESH_MEMBER_TOKEN',
  MEMBER_TOKEN_REFRESHED: 'MEMBER_TOKEN_REFRESHED',
  MEMBER_TOKEN_REFRESH_FAILED: 'MEMBER_TOKEN_REFRESH_FAILED',
};

export const getInitialState = (permissionStatus, connectedCompanyPage, isMemberTokenActive, isReAuthOnly) => ({
  permissionStatus, // REQUESTED, APPROVED, REJECTED
  checkDateTime: null, // The time when the permission status was checked
  companyPages: null, // List of linkedin company pages
  selectedCompanyPage: connectedCompanyPage, // Selected company page object
  connectedCompanyPage, // Connected company page object
  authorizationData: null, // Authorization data for linkedin
  isAuthenticating: false,
  authenticationError: null,
  isReadyToLink: false,
  isSavingCompanyPage: false,
  saveCompanyPageError: null,
  isLoadingPermissionStatus: false,
  loadPermissionStatusError: null,
  isResendingPermissionRequest: false,
  resendPermissionRequestError: null,
  isReAuthOnly,
  isMemberTokenActive,
  needRefreshMemberToken: isMemberTokenActive === false,
  isRefreshingToken: false,
  refreshTokenError: null,
});

export function companyPageLinkingReducer(state, action) {
  switch (action.type) {
    case COMPANY_PAGE_LINKING_ACTIONS.START_AUTHENTICATION: {
      return {
        ...state,
        isAuthenticating: true,
        authenticationError: null,
      };
    }
    case COMPANY_PAGE_LINKING_ACTIONS.COMPANY_PAGES_FETCHED: {
      const { companyPages, authorizationData } = action;
      const selectedCompanyPage = null;
      return {
        ...state,
        authorizationData,
        companyPages,
        selectedCompanyPage,
        isAuthenticating: false,
        isAuthorized: true,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.AUTHENTICATION_FAILED: {
      const { authenticationError } = action;
      return {
        ...state,
        authenticationError,
        isAuthenticating: false,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.UPDATE_SELECTED_COMPANY_PAGE: {
      const { selectedCompanyPage } = action;
      return {
        ...state,
        selectedCompanyPage,
        saveCompanyPageError: null,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.CONFIRM_LINK_COMPANY_PAGE: {
      return {
        ...state,
        isReadyToLink: true,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.START_SAVING_COMPANY_PAGE: {
      return {
        ...state,
        isSavingCompanyPage: true,
        saveCompanyPageError: null,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.COMPANY_PAGE_SAVED: {
      const { connectedCompanyPage, permissionStatus } = action;
      return {
        ...state,
        connectedCompanyPage,
        permissionStatus: permissionStatus || PERMISSION_STATUS.REQUESTED, // The default status should be PENDING after saving a company page
        saveCompanyPageError: null,
        isSavingCompanyPage: false,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.COMPANY_PAGE_SAVE_FAILED: {
      const { saveCompanyPageError } = action;
      return {
        ...state,
        saveCompanyPageError,
        isSavingCompanyPage: false,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.LOAD_PERMISSION_STATUS: {
      return {
        ...state,
        isLoadingPermissionStatus: true,
        loadPermissionStatusError: null,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.PERMISSION_STATUS_LOADED: {
      const { permissionStatus } = action;
      return {
        ...state,
        permissionStatus,
        checkDateTime: new Date(),
        loadPermissionStatusError: null,
        isLoadingPermissionStatus: false,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.PERMISSION_STATUS_LOAD_FAILED: {
      const { loadPermissionStatusError } = action;
      return {
        ...state,
        loadPermissionStatusError,
        isLoadingPermissionStatus: false,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.GO_BACK_AND_RESET_COMPANY_PAGE: {
      return {
        ...state,
        isReadyToLink: false,
        selectedCompanyPage: null,
        permissionStatus: PERMISSION_STATUS.NOT_FOUND,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.RESEND_PERMISSION_REQUEST: {
      return {
        ...state,
        isResendingPermissionRequest: true,
        resendPermissionRequestError: null,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.PERMISSION_REQUEST_RESENT: {
      return {
        ...state,
        permissionStatus: PERMISSION_STATUS.REQUESTED, // The default status should be PENDING after resending a permission request
        resendPermissionRequestError: null,
        isResendingPermissionRequest: false,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.PERMISSION_REQUEST_RESEND_FAILED: {
      const { resendPermissionRequestError } = action;
      return {
        ...state,
        resendPermissionRequestError,
        isResendingPermissionRequest: false,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.CLEAN_ERRORS: {
      return {
        ...state,
        authenticationError: null,
        saveCompanyPageError: null,
        loadPermissionStatusError: null,
        resendPermissionRequestError: null,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.START_REFRESH_MEMBER_TOKEN: {
      return {
        ...state,
        isRefreshingToken: true,
        refreshTokenError: null,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.MEMBER_TOKEN_REFRESHED: {
      return {
        ...state,
        isMemberTokenActive: true,
        refreshTokenError: null,
      };
    }

    case COMPANY_PAGE_LINKING_ACTIONS.MEMBER_TOKEN_REFRESH_FAILED: {
      const { refreshTokenError } = action;
      return {
        ...state,
        isMemberTokenActive: false,
        refreshTokenError,
        isRefreshingToken: false,
      };
    }

    default: throw new Error(`Invalid company page linking action: ${action.type}`);
  }
}
